import { css, cva } from '@/styled-system/css';
// oxlint-disable-next-line constellation/no-styled-factory -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change
import { styled } from '@/styled-system/jsx';

import { Table } from '@zillow/constellation-10';

import { tableCellHoverStyles } from '../../components/properties/styles';

const TABLE_CELL_BORDER = '1px solid {colors.Table.Cell.border}';

export const PROPERTY_ROW_WIDTHS = {
  FIRST_CELL: '350px',
  MIDDLE_CELL: '189px',
  LAST_CELL: '72px',
} as const;

/** multiplex expand/collapse chevron column: Icon `size="sm"` plus padding (`spacing.800`). */
const CHEVRON_GUTTER = '1200' as const;

const IMAGE_DIMENSIONS = {
  WIDTH: 100, // max-width of image
  HEIGHT: 60, // max-height of image
};

export const CELL_DIMENSIONS = {
  WIDTH: 200, // max-width of cell
  HEIGHT: 63, // min-height of cell
};

export const propertyPhotoContainerStyles = css.raw({
  display: { base: 'none', md: 'flex' },
  marginRight: '300',
  /* bypass the need for text to properly align */
  '&:before': {
    // eslint-disable-next-line curly-quotes/no-straight-quotes
    content: '"hidden"',
    display: 'flex',
    height: 'zero',
    fontSize: '0px',
  },
});

export const propertyPhotoRecipe = cva({
  base: {
    margin: '0 auto',
    borderRadius: '200',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
  },
  variants: {
    hasPhoto: {
      true: {
        backgroundSize: 'cover',
      },
      false: {
        backgroundSize: 'contain',
      },
    },
    isParentPropertyRow: {
      true: {
        width: '100%',
        height: '100%',
      },
      false: {
        width: `${IMAGE_DIMENSIONS.WIDTH}px`,
        height: `${IMAGE_DIMENSIONS.HEIGHT}px`,
      },
    },
  },
  defaultVariants: {
    hasPhoto: false,
    isParentPropertyRow: false,
  },
});

export const cellAddressContainer = css.raw({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'stretch',
  minWidth: 0,
  width: '100%',
});

export const chevronContainerStyles = css.raw({
  display: 'flex',
  flexDirection: 'row',
  flexGrow: 0,
  flexShrink: 0,
  alignItems: 'center',
  justifyContent: 'center',
  width: CHEVRON_GUTTER,
  minWidth: CHEVRON_GUTTER,
  maxWidth: CHEVRON_GUTTER,
});

// address and summary container parent container
export const flexContainerStyles = css.raw({
  display: 'flex',
  minHeight: `${CELL_DIMENSIONS.HEIGHT}px`,
  alignItems: 'center',
  /* bypass the need for text to properly align */
  '&:before': {
    // eslint-disable-next-line curly-quotes/no-straight-quotes
    content: '"hidden"',
    display: 'flex',
    height: 'zero',
    fontSize: '0px',
  },
});

export const propertyAddressStyles = css.raw({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  maxWidth: '100%',
  '& > *': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

// oxlint-disable-next-line constellation/no-styled-factory -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change
export const TableHeaderCell = styled(Table.HeaderCell, {
  base: {
    fontSize: 'body-sm',
    fontWeight: 'bold',
    padding: '{spacing.tighter}',
    display: { base: 'none', lg: 'table-cell' },
    width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,

    '&:first-child': {
      paddingLeft: '{spacing.loose}',
      width: PROPERTY_ROW_WIDTHS.FIRST_CELL,
      display: 'table-cell',
    },

    '&:last-child': {
      width: PROPERTY_ROW_WIDTHS.LAST_CELL,
      display: 'table-cell',
    },

    md: {
      width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,
    },
  },
});

// oxlint-disable-next-line constellation/no-styled-factory -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change
export const TableCell = styled(Table.Cell, {
  base: {
    // oxlint-disable-next-line constellation/no-raw-spacing-values -- `height: 105px` falls between `2600` (104px) and `3600` (144px) on the numeric sizes scale
    height: '105px',

    padding: '{spacing.300} {spacing.400}',

    '&:first-child': {
      width: 'auto',
      maxWidth: 'none',
    },

    '&:last-child': {
      width: '1500',
      minWidth: '1500',
      maxWidth: '1500',
    },

    md: {
      '&:last-child': {
        paddingRight: '400',
      },
    },

    lg: {
      paddingLeft: '200',
      width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,
      '&:first-child': {
        // paddingLeft: '600',
        paddingLeft: '0',

        width: PROPERTY_ROW_WIDTHS.FIRST_CELL,
      },
      '&:last-child': {
        paddingRight: '200',
        width: PROPERTY_ROW_WIDTHS.LAST_CELL,
      },
    },
  },
});

export const tagStyles = css.raw({
  display: 'flex',
  width: 'fit-content',
  textWrap: 'wrap',
});

/** Nested unit rows (not the multiplex parent) use a subtle background on table cells. */
export const groupUnitChildRowCellBackgroundCss = css.raw({
  backgroundColor: '{colors.bg.softest}',
});

/** Nested unit rows: no table cell padding (inset comes from inner layout). */
export const groupUnitChildRowTableCellPaddingCss = css.raw({
  height: '1400',
});

export const cellAnchorStyles = css.raw({
  color: 'text.neutral',
  textDecoration: 'none',
  'td:hover &': {
    color: 'blue.500',
    textDecoration: 'underline',
  },
});

const leftDataCellRecipe = cva({
  base: {
    position: { base: 'relative', lg: 'sticky' },
    left: { base: 'auto', lg: 0 },
    zIndex: 2,
    boxSizing: 'border-box',
    transition: 'background-color 0.1s ease-out',
    verticalAlign: 'middle',
    overflow: 'hidden', // Prevent cell content from overflowing

    // When the parent tr is being hovered, the cell should be highlighted
    'tbody > *:hover tr &': {
      backgroundColor: 'bg.action.neutral.alt.hover',
    },

    // Bottom row is always has border
    'tbody > *:last-child tr:last-of-type &': {
      borderBottom: TABLE_CELL_BORDER,
    },
  },
  variants: {
    scrolled: {
      true: {
        boxShadow: '2px 4px 8px 0px rgba(0, 0, 0, {opacity.15})',
        borderRight: '2px solid {colors.border.muted-alt}',
      },
    },
  },
});

export const leftDataCellStyles = (isScrolledToLeft: boolean) => leftDataCellRecipe.raw({ scrolled: isScrolledToLeft });

export const rightDataCellStyles = css.raw({
  transition: 'background-color 0.1s ease-out',

  // When the parent tr is being hovered, the cell should be highlighted
  'tbody > *:hover tr &': {
    backgroundColor: 'bg.action.neutral.alt.hover',
  },

  // Bottom row is always has border
  'tbody > *:last-child tr:last-of-type &': {
    borderBottom: TABLE_CELL_BORDER,
  },
});

// oxlint-disable-next-line constellation/no-styled-factory -- ESLint's pandaLint set this warn-only; oxlint's oxlintPandaLint sets it error, no code change
export const HiddenMobileTableCell = styled(Table.Cell, {
  base: {
    display: { base: 'none', lg: 'table-cell' },
    width: PROPERTY_ROW_WIDTHS.MIDDLE_CELL,
    ...tableCellHoverStyles,
  },
});
